import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import LegalBanner from "@/components/LegalBanner";
import Footer from "@/components/Footer";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Field, CurrencyInput, PercentInput, TermSelect, CreditSelect, BodySelect } from "@/components/calc/fields";
import { Tile } from "@/components/home/HeroCalc";
import { friendlyCalcError } from "@/lib/api";
import { track } from "@/lib/analytics";
import { Seo, breadcrumbLd, SITE_URL } from "@/lib/seo";
import { CALC_TITLES } from "./configs";

function FieldRenderer({ f, value, onChange }) {
  const id = `calc-${f.key}`;
  switch (f.type) {
    case "currency": return <CurrencyInput id={id} value={value} onChange={onChange} />;
    case "percent": return <PercentInput id={id} value={value} onChange={onChange} />;
    case "term": return <TermSelect id={id} value={value} onChange={onChange} />;
    case "credit": return <CreditSelect id={id} value={value} onChange={onChange} />;
    case "body": return <BodySelect id={id} value={value} onChange={onChange} />;
    default:
      return (
        <input id={id} data-testid={id} type="text" inputMode="decimal" autoComplete="off"
          className="w-full h-13 px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 text-base focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-colors"
          value={value} onChange={(e) => onChange(e.target.value)} />
      );
  }
}

function Viz({ viz }) {
  if (!viz) return null;
  if (viz.type === "splitBar") {
    const total = viz.principal + viz.interest || 1;
    return (
      <div data-testid="viz-split-bar" className="mt-5">
        <div className="h-4 rounded-full overflow-hidden flex">
          <div className="bg-blue-600" style={{ width: `${(viz.principal / total) * 100}%` }} />
          <div className="bg-stone-300" style={{ width: `${(viz.interest / total) * 100}%` }} />
        </div>
        <div className="flex gap-6 mt-2 text-xs text-stone-600">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-blue-600"></span> Principal</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-stone-300"></span> Interest</span>
        </div>
      </div>
    );
  }
  if (viz.type === "termTable") {
    return (
      <div data-testid="viz-term-table" className="mt-5 overflow-hidden rounded-2xl border border-stone-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-stone-50 text-left text-xs uppercase tracking-wider text-stone-500">
              <th className="px-4 py-2.5 font-semibold">Term</th>
              <th className="px-4 py-2.5 font-semibold">Payment</th>
              <th className="px-4 py-2.5 font-semibold">Total interest</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {viz.rows.map((r) => (
              <tr key={r.term}>
                <td className="px-4 py-2.5 font-semibold text-stone-900">{r.term} mo</td>
                <td className="px-4 py-2.5 text-stone-700">${Math.round(r.payment).toLocaleString()}/mo</td>
                <td className="px-4 py-2.5 text-stone-700">${Math.round(r.interest).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (viz.type === "dtiBars") {
    return (
      <div data-testid="viz-dti-bars" className="mt-5 space-y-3">
        {viz.bars.map((b, i) => (
          <div key={b.label}>
            <div className="flex justify-between text-xs font-medium text-stone-600 mb-1">
              <span>{b.label}</span><span className="font-bold text-stone-900">{b.value.toFixed(1)}%</span>
            </div>
            <div className="h-3 rounded-full bg-stone-100 overflow-hidden">
              <div className={`h-full rounded-full ${i === 2 ? "bg-emerald-500" : "bg-blue-600"}`} style={{ width: `${Math.min(100, b.value)}%` }} />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (viz.type === "affordRange") {
    return (
      <div data-testid="viz-afford-range" className="mt-5">
        <div className="relative h-3 rounded-full bg-stone-100">
          <div className="absolute h-full rounded-full bg-blue-600/30" style={{ width: "100%" }} />
          <div className="absolute h-full rounded-full bg-blue-600" style={{ width: `${viz.max ? (viz.comfortable / viz.max) * 100 : 0}%` }} />
        </div>
        <div className="flex justify-between mt-2 text-xs">
          <span className="font-semibold text-blue-700">Comfortable: ${Math.round(viz.comfortable).toLocaleString()}</span>
          <span className="font-semibold text-stone-500">Max: ${Math.round(viz.max).toLocaleString()}</span>
        </div>
      </div>
    );
  }
  if (viz.type === "ownershipBars") {
    const total = viz.parts.reduce((s, p) => s + p.value, 0) || 1;
    const COLORS = ["bg-blue-600", "bg-emerald-500", "bg-amber-400", "bg-stone-400", "bg-stone-300"];
    return (
      <div data-testid="viz-ownership-bars" className="mt-5">
        <div className="h-4 rounded-full overflow-hidden flex">
          {viz.parts.map((p, i) => (
            <div key={p.label} className={COLORS[i % COLORS.length]} style={{ width: `${(p.value / total) * 100}%` }} />
          ))}
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-2 text-xs text-stone-600">
          {viz.parts.map((p, i) => (
            <span key={p.label} className="flex items-center gap-1.5">
              <span className={`w-2.5 h-2.5 rounded-sm ${COLORS[i % COLORS.length]}`}></span>
              {p.label}: ${Math.round(p.value).toLocaleString()}/mo
            </span>
          ))}
        </div>
      </div>
    );
  }
  return null;
}

export default function CalcPage({ config }) {
  const defaults = Object.fromEntries(config.fields.map((f) => [f.key, f.def ?? ""]));
  const [values, setValues] = useState(defaults);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [started, setStarted] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [config.path]);

  const setV = (k) => (v) => {
    if (!started) { setStarted(true); track("calculator_started", { page: config.slug }); }
    setValues({ ...values, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const data = await config.compute(values);
      setResult(data);
      track("calculator_completed", { page: config.slug });
    } catch (err) { setError(friendlyCalcError(err)); }
    finally { setLoading(false); }
  };

  const jsonLd = [
    breadcrumbLd([
      { name: "Home", path: "/" },
      { name: "Calculators", path: "/#calculators" },
      { name: config.h1, path: config.path },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: config.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-white text-stone-900 font-sans antialiased" data-testid={`page-${config.slug}`}>
      <Seo title={config.title} description={config.description} path={config.path} jsonLd={jsonLd} />
      <LegalBanner />
      <Header />
      <main className="py-12 lg:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="text-xs text-stone-400 mb-6">
            <Link to="/" className="hover:text-stone-700">Home</Link>
            <span className="mx-1.5">/</span>
            <a href="/#calculators" className="hover:text-stone-700">Calculators</a>
            <span className="mx-1.5">/</span>
            <span className="text-stone-600">{config.h1}</span>
          </nav>

          <h1 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight mb-3">{config.h1}</h1>
          <p className="text-stone-600 text-base leading-relaxed mb-8">{config.intro}</p>

          <div className="bg-white rounded-3xl border border-stone-200 shadow-xl shadow-stone-900/5 p-6 sm:p-8 mb-8">
            <form onSubmit={submit} data-testid="calc-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {config.fields.map((f) => (
                  <Field key={f.key} label={f.label} help={f.help} htmlFor={`calc-${f.key}`}>
                    <FieldRenderer f={f} value={values[f.key]} onChange={setV(f.key)} />
                  </Field>
                ))}
              </div>
              {error && <p data-testid="calc-error" className="text-sm text-rose-600 mt-3">{error}</p>}
              <button type="submit" disabled={loading} data-testid="calc-submit" className="w-full mt-5 h-13 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold text-base transition-colors">
                {loading ? "Calculating..." : config.ctaLabel}
              </button>
              <p className="text-xs text-stone-500 text-center mt-2.5">Free calculation • No account required • Educational estimate</p>
            </form>

            {result && (
              <div data-testid="calc-result" className="mt-8 pt-8 border-t border-stone-100">
                <div className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-1">{result.primary.label}</div>
                <div className="text-5xl font-extrabold text-stone-900 mb-6" data-testid="calc-primary" style={{ fontFeatureSettings: '"tnum" 1' }}>{result.primary.value}</div>
                {result.tiles?.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {result.tiles.map((t, i) => <Tile key={t.label} label={t.label} value={t.value} testid={`calc-tile-${i}`} />)}
                  </div>
                )}
                <Viz viz={result.viz} />
                {result.note && <p className="text-xs text-stone-500 leading-relaxed mt-4">{result.note}</p>}

                <div className="rounded-2xl bg-blue-50 border border-blue-100 p-5 mt-6">
                  <p className="text-sm text-stone-700 leading-relaxed mb-3">
                    Your numbers tell you what the loan costs. <strong className="text-stone-900">But does this vehicle actually fit your finances?</strong>
                  </p>
                  <Link to="/#calculator" data-testid="fit-bridge-cta" className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-700 hover:text-blue-800">
                    Check your DriveClear Fit &rarr;
                  </Link>
                </div>
              </div>
            )}
          </div>

          <h2 className="text-xl font-extrabold text-stone-900 tracking-tight mb-4">Common questions</h2>
          <Accordion type="single" collapsible className="space-y-3 mb-10">
            {config.faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`calc-faq-${i}`} className="px-6 rounded-2xl bg-[#faf9f7] border border-stone-200">
                <AccordionTrigger className="text-sm font-bold text-stone-900 hover:no-underline hover:text-blue-700 py-4">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-stone-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <h2 className="text-xl font-extrabold text-stone-900 tracking-tight mb-4">Related calculators</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {config.related.map((r) => (
              <Link key={r} to={r} data-testid={`related-${r.slice(1)}`} className="rounded-2xl border border-stone-200 p-4 text-sm font-semibold text-stone-800 hover:border-blue-300 hover:text-blue-700 transition-colors">
                {CALC_TITLES[r]} &rarr;
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export { SITE_URL };
